import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { getColumnWidth, isValidColumnSize } from '@aibees/shared/utils';
import type { AdvanceTableColumn } from '../types';

/**
 * Derives a stable column id from an AdvanceTableColumn definition.
 * Prefers `key`, then falls back to `id` (cast to string).
 */
function deriveColumnId(col: AdvanceTableColumn): string {
  return (col.key ?? col.id)?.toString() ?? '';
}

/**
 * Converts the project's AdvanceTableColumn[] to TanStack ColumnDef[].
 *
 * The mapping preserves all existing column metadata (size, sticky, sortable,
 * label, render, etc.) so that rendering code can read them from `columnDef.meta`.
 *
 * This is designed to be called inside a `useMemo` in the context provider.
 */
export function adaptColumns<T = any>(
  columns: AdvanceTableColumn<T>[]
): ColumnDef<T, any>[] {
  return columns.map((col) => {
    const id = deriveColumnId(col);
    const sizeInPx =
      col.size && isValidColumnSize(col.size)
        ? getColumnWidth(col.size)
        : typeof col.width === 'number'
        ? col.width
        : undefined;

    return {
      id,
      accessorFn: (row: T) => (row as Record<string, any>)[id],
      header: () => col.label ?? '',
      cell: col.render
        ? (info) =>
            col.render!(info.getValue(), info.row.original, info.row.index)
        : (info) => info.getValue() ?? '-',
      enableSorting: !!col.sortable,
      ...(sizeInPx != null && { size: sizeInPx }),
      meta: {
        originalColumn: col,
        align: col.align,
        isSticky: col.isSticky,
        expandable: col.expandable,
        className: col.className,
        headerClassName: col.headerClassName,
        metadata: col.metadata,
        tooltip: col.tooltip,
        hide: col.hide,
        order: col.order,
        disabledInColumnModifier: col.disabledInColumnModifier,
        excludeFromColumnModifier: col.excludeFromColumnModifier,
        extraContentRender: col.extraContentRender,
      },
    } satisfies ColumnDef<T, any>;
  });
}

/**
 * Builds a TanStack columnVisibility map from AdvanceTableColumn[].
 * Columns with `hide: true` are set to `false` (hidden).
 */
export function buildColumnVisibility(
  columns: AdvanceTableColumn[]
): VisibilityState {
  const visibility: VisibilityState = {};
  for (const col of columns) {
    const id = deriveColumnId(col);
    if (id && col.hide) {
      visibility[id] = false;
    }
  }
  return visibility;
}
